import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaFolder, FaFileAlt, FaTerminal, FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import data from './data';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/* ============================================
   ANIMATIONS
   ============================================ */

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const dockBounce = keyframes`
  0%, 100% { transform: translateY(0); }
  30% { transform: translateY(-20px); }
  50% { transform: translateY(-10px); }
  70% { transform: translateY(-15px); }
`;

const iconPop = keyframes`
  from { opacity: 0; transform: scale(0.5); }
  to { opacity: 1; transform: scale(1); }
`;

/* ============================================
   DESKTOP SHELL
   ============================================ */

const DesktopWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9000;
  background: radial-gradient(ellipse at 30% 20%, #1a1a3e 0%, #0a0e1a 50%, #050510 100%);
  animation: ${fadeIn} 0.4s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  user-select: none;
`;

/* --- Menu Bar --- */
const MenuBar = styled.div`
  height: 28px;
  background: rgba(30, 30, 50, 0.85);
  backdrop-filter: blur(20px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
  z-index: 100;
`;

const MenuLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 13px;
  color: #e2e8f0;
  font-weight: 600;

  span {
    font-weight: 400;
    color: #94a3b8;
    cursor: default;
  }
`;

const AppleLogo = styled.span`
  font-size: 16px;
  cursor: default;
`;

const MenuRight = styled.div`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 12px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 16px;
`;

/* --- Desktop Area --- */
const DesktopArea = styled.div`
  flex: 1;
  position: relative;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  padding-top: 16px;
`;

const DesktopIcon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  width: 80px;
  transition: background 0.15s ease;
  animation: ${iconPop} 0.3s ease forwards;
  animation-delay: ${props => props.delay || '0s'};
  opacity: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  &:active {
    background: rgba(255, 255, 255, 0.12);
  }

  svg {
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
  }
`;

const IconLabel = styled.span`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 11px;
  color: #fff;
  text-align: center;
  text-shadow: 0 1px 3px rgba(0,0,0,0.6);
  line-height: 1.3;
  word-break: break-word;
`;

/* --- Dock --- */
const DockContainer = styled.div`
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: flex-end;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  backdrop-filter: blur(20px);
  z-index: 100;
`;

const DockSeparator = styled.div`
  width: 1px;
  height: 36px;
  background: rgba(255, 255, 255, 0.15);
  margin: 0 6px;
  align-self: center;
`;

const DockItem = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease;
  position: relative;
  background: ${props => props.bg || 'rgba(255,255,255,0.06)'};
  border: 1px solid rgba(255, 255, 255, 0.08);

  &:hover {
    transform: scale(1.3) translateY(-8px);
  }

  svg {
    color: ${props => props.iconColor || '#e2e8f0'};
  }

  ${props => props.active && `
    &::after {
      content: '';
      position: absolute;
      bottom: -6px;
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: #fff;
    }
  `}

  ${props => props.bounce && css`
    animation: ${dockBounce} 0.6s ease;
  `}
`;

const DockTooltip = styled.div`
  position: absolute;
  bottom: 58px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 10px;
  background: rgba(30, 30, 50, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 11px;
  color: #fff;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;

  ${DockItem}:hover & {
    opacity: 1;
  }
`;

/* --- Window --- */
const WindowOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  animation: ${fadeIn} 0.15s ease;
`;

const WindowFrame = styled.div`
  width: ${props => props.width || '700px'};
  max-width: 90vw;
  max-height: 75vh;
  background: rgba(30, 33, 48, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 25px 70px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.05);
  display: flex;
  flex-direction: column;
  animation: ${slideUp} 0.3s ease;
`;

const WindowTitleBar = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: rgba(40, 42, 60, 0.9);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
`;

const WindowTrafficLights = styled.div`
  display: flex;
  gap: 8px;
  margin-right: 12px;
`;

const WindowDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.color};
  opacity: 0.8;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  transition: opacity 0.15s;

  &:hover {
    opacity: ${props => props.clickable ? '1' : '0.8'};
  }
`;

const WindowTitle = styled.span`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 13px;
  color: #94a3b8;
  flex: 1;
  text-align: center;
  margin-right: 40px;
`;

const WindowBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255,255,255,0.1);
    border-radius: 3px;
  }
`;

/* --- Finder Grid (Projects) --- */
const FinderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FinderCard = styled.a`
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.07);
    border-color: rgba(34, 211, 238, 0.2);
    transform: translateY(-2px);
  }
`;

const FinderCardImage = styled.div`
  width: 100%;
  height: 140px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const FinderCardBody = styled.div`
  padding: 12px;
`;

const FinderCardTitle = styled.div`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #e2e8f0;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  svg {
    color: #64748b;
    flex-shrink: 0;
  }
`;

const FinderCardDesc = styled.div`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 11px;
  color: #94a3b8;
  line-height: 1.5;
  margin-bottom: 8px;
`;

const FinderTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const FinderTag = styled.span`
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: #22d3ee;
  background: rgba(34, 211, 238, 0.08);
  border: 1px solid rgba(34, 211, 238, 0.12);
  padding: 2px 6px;
  border-radius: 3px;
`;

/* --- TextEdit (About) --- */
const TextContent = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  color: #e2e8f0;
  line-height: 1.8;
  white-space: pre-wrap;

  .highlight {
    color: #22d3ee;
  }

  .muted {
    color: #64748b;
  }
`;

/* --- Resume Preview --- */
const ResumePreviewWrapper = styled.div`
  display: flex;
  justify-content: center;

  .react-pdf__Page canvas {
    max-width: 100% !important;
    height: auto !important;
  }
`;

/* ============================================
   COMPONENT
   ============================================ */

const DesktopMode = ({ onRestore }) => {
  const [openWindow, setOpenWindow] = useState(null);
  const [clock, setClock] = useState('');
  const [bouncingDock, setBouncingDock] = useState(null);

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setClock(now.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }));
    };
    updateClock();
    const interval = setInterval(updateClock, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleDockClick = (id) => {
    setBouncingDock(id);
    setTimeout(() => setBouncingDock(null), 600);
    if (id === 'portfolio') {
      onRestore();
    } else if (id === 'projects') {
      setOpenWindow('projects');
    } else if (id === 'terminal') {
      onRestore();
    }
  };

  const handleDesktopIconDblClick = (id) => {
    if (id === 'portfolio') {
      onRestore();
    } else {
      setOpenWindow(id);
    }
  };

  const getLinkIcon = (url) => {
    if (url && url.includes('github.com')) return <FaGithub size={12} />;
    return <FaExternalLinkAlt size={10} />;
  };

  const renderWindow = () => {
    if (!openWindow) return null;

    if (openWindow === 'projects') {
      return (
        <WindowOverlay onClick={(e) => e.target === e.currentTarget && setOpenWindow(null)}>
          <WindowFrame width="820px">
            <WindowTitleBar>
              <WindowTrafficLights>
                <WindowDot color="#ff5f57" clickable onClick={() => setOpenWindow(null)} />
                <WindowDot color="#febc2e" />
                <WindowDot color="#28c840" />
              </WindowTrafficLights>
              <WindowTitle>Projects</WindowTitle>
            </WindowTitleBar>
            <WindowBody>
              <FinderGrid>
                {data.map((project, i) => (
                  <FinderCard key={i} href={project.url} target="_blank" rel="noopener noreferrer">
                    <FinderCardImage>
                      <img src={project.image} alt={project.caption} loading="lazy" />
                    </FinderCardImage>
                    <FinderCardBody>
                      <FinderCardTitle>
                        <span>{project.caption}</span>
                        {getLinkIcon(project.url)}
                      </FinderCardTitle>
                      <FinderCardDesc>{project.description}</FinderCardDesc>
                      <FinderTags>
                        {project.tech && project.tech.map((t, j) => (
                          <FinderTag key={j}>{t}</FinderTag>
                        ))}
                      </FinderTags>
                    </FinderCardBody>
                  </FinderCard>
                ))}
              </FinderGrid>
            </WindowBody>
          </WindowFrame>
        </WindowOverlay>
      );
    }

    if (openWindow === 'about') {
      return (
        <WindowOverlay onClick={(e) => e.target === e.currentTarget && setOpenWindow(null)}>
          <WindowFrame width="550px">
            <WindowTitleBar>
              <WindowTrafficLights>
                <WindowDot color="#ff5f57" clickable onClick={() => setOpenWindow(null)} />
                <WindowDot color="#febc2e" />
                <WindowDot color="#28c840" />
              </WindowTrafficLights>
              <WindowTitle>about.txt</WindowTitle>
            </WindowTitleBar>
            <WindowBody>
              <TextContent>
{`> `}<span className="highlight">Dev Kansara</span>{`
> Software Engineer @ XOPS

`}<span className="muted">{`# Education`}</span>{`
  MS Computer Science, USC '25
  BE Information Technology, Mumbai '23

`}<span className="muted">{`# Currently`}</span>{`
  Building scalable full-stack applications
  and AI-driven enterprise tools at XOPS.

`}<span className="muted">{`# Stack`}</span>{`
  Go | React | Python | AWS | Docker
  GraphQL | DynamoDB | Pinecone | Kafka

`}<span className="muted">{`# Links`}</span>{`
  github.com/devkansara
  linkedin.com/in/devkansara`}
              </TextContent>
            </WindowBody>
          </WindowFrame>
        </WindowOverlay>
      );
    }

    if (openWindow === 'resume') {
      return (
        <WindowOverlay onClick={(e) => e.target === e.currentTarget && setOpenWindow(null)}>
          <WindowFrame width="600px">
            <WindowTitleBar>
              <WindowTrafficLights>
                <WindowDot color="#ff5f57" clickable onClick={() => setOpenWindow(null)} />
                <WindowDot color="#febc2e" />
                <WindowDot color="#28c840" />
              </WindowTrafficLights>
              <WindowTitle>Resume.pdf</WindowTitle>
            </WindowTitleBar>
            <WindowBody>
              <ResumePreviewWrapper>
                <Document
                  file={require('../src/Images/resume.pdf')}
                  loading={<div style={{ color: '#94a3b8', padding: '40px', textAlign: 'center', fontFamily: 'JetBrains Mono, monospace', fontSize: '13px' }}>Loading resume...</div>}
                >
                  <Page pageNumber={1} width={520} renderTextLayer={false} renderAnnotationLayer={false} />
                </Document>
              </ResumePreviewWrapper>
            </WindowBody>
          </WindowFrame>
        </WindowOverlay>
      );
    }

    return null;
  };

  return (
    <DesktopWrapper>
      {/* Menu Bar */}
      <MenuBar>
        <MenuLeft>
          <AppleLogo></AppleLogo>
          <span>Finder</span>
          <span>File</span>
          <span>Edit</span>
          <span>View</span>
        </MenuLeft>
        <MenuRight>
          <span>☁ 100%</span>
          <span>{clock}</span>
        </MenuRight>
      </MenuBar>

      {/* Desktop Icons */}
      <DesktopArea>
        <DesktopIcon delay="0.1s" onDoubleClick={() => handleDesktopIconDblClick('projects')}>
          <FaFolder size={48} color="#54aef0" />
          <IconLabel>Projects</IconLabel>
        </DesktopIcon>
        <DesktopIcon delay="0.2s" onDoubleClick={() => handleDesktopIconDblClick('resume')}>
          <FaFileAlt size={48} color="#e74c3c" />
          <IconLabel>Resume.pdf</IconLabel>
        </DesktopIcon>
        <DesktopIcon delay="0.3s" onDoubleClick={() => handleDesktopIconDblClick('about')}>
          <FaFileAlt size={48} color="#94a3b8" />
          <IconLabel>about.txt</IconLabel>
        </DesktopIcon>
        <DesktopIcon delay="0.4s" onDoubleClick={() => handleDesktopIconDblClick('portfolio')}>
          <FaTerminal size={48} color="#22d3ee" />
          <IconLabel>Portfolio.app</IconLabel>
        </DesktopIcon>
      </DesktopArea>

      {/* Open Window */}
      {renderWindow()}

      {/* Dock */}
      <DockContainer>
        <DockItem
          bg="linear-gradient(135deg, #1a1a3e, #2d2d5e)"
          iconColor="#22d3ee"
          onClick={() => handleDockClick('terminal')}
          bounce={bouncingDock === 'terminal'}
          title="Terminal"
        >
          <DockTooltip>Terminal</DockTooltip>
          <FaTerminal size={24} />
        </DockItem>
        <DockItem
          bg="linear-gradient(135deg, #1a3a5e, #2a5a8e)"
          iconColor="#54aef0"
          active
          onClick={() => handleDockClick('projects')}
          bounce={bouncingDock === 'projects'}
        >
          <DockTooltip>Projects</DockTooltip>
          <FaFolder size={24} />
        </DockItem>
        <DockSeparator />
        <DockItem
          bg="linear-gradient(135deg, #0f2a1a, #1a4a2a)"
          iconColor="#28c840"
          onClick={() => handleDockClick('portfolio')}
          bounce={bouncingDock === 'portfolio'}
        >
          <DockTooltip>Back to Portfolio</DockTooltip>
          <span style={{ fontSize: '18px' }}>{'</>'}</span>
        </DockItem>
      </DockContainer>
    </DesktopWrapper>
  );
};

export default DesktopMode;
